import { useId, useMemo } from 'react';
import { BarChart as RechartsBar, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import type { ColumnDef, HistogramMapping, VisualizationConfig } from '@deckspace/types';
import { getColorsForIntent, getThemeColors } from '../../theme/index.js';
import { createChartFormatter } from '../../utils/chart-formatter.js';
import { ChartTooltip } from '../../components/ChartTooltip.js';
import { prepHistogramSeries } from './utils.js';

interface Props {
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
  mapping: HistogramMapping;
  config: VisualizationConfig;
  width?: number;
  height?: number;
}

export function Histogram({ rows, columnDefs, mapping, config, width, height }: Props) {
  const uid = useId();

  const series = useMemo(
    () =>
      rows.length === 0
        ? { values: [] as number[], labels: undefined as string[] | undefined }
        : prepHistogramSeries(rows, columnDefs, mapping),
    [rows, columnDefs, mapping]
  );

  const chartData = useMemo(
    () =>
      series.values.map((value, index) => ({
        name: series.labels?.[index] ?? `${index}`,
        frequency: value,
      })),
    [series.values, series.labels]
  );

  if (!series.values.length) {
    return <div className="text-muted-foreground">No data available</div>;
  }

  const colors = getColorsForIntent(config.intent, 1);
  const themeColors = getThemeColors();

  const formatValue = createChartFormatter([]);

  return (
    <RechartsBar
      width={width ?? 400}
      height={height ?? 300}
      data={chartData}
      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
    >
      <defs>
        <linearGradient id={`histogram-gradient-0-${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={colors[0]} stopOpacity={0.95} />
          <stop offset="100%" stopColor={colors[0]} stopOpacity={0.7} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke={themeColors.border} />
      <XAxis
        dataKey="name"
        tick={{ fontSize: 12, fill: themeColors.textSecondary }}
        stroke={themeColors.textMuted}
      />
      <YAxis
        width="auto"
        tick={{ fontSize: 12, fill: themeColors.textSecondary }}
        stroke={themeColors.textMuted}
        tickFormatter={(value) => formatValue(value, 0)}
        label={{ value: 'Frequency', angle: -90, position: 'insideLeft' }}
      />
      <Tooltip content={<ChartTooltip formatter={formatValue} />} />
      <Bar
        dataKey="frequency"
        fill={`url(#histogram-gradient-0-${uid})`}
        stroke={colors[0]}
        strokeWidth={2}
        radius={[4, 4, 0, 0]}
        isAnimationActive={true}
      />
    </RechartsBar>
  );
}
